import React from "react";
import { useDropzone as useReactDropzone } from "react-dropzone";
import {
  Controller,
  FieldPath,
  FieldPathByValue,
  FieldValues,
  UseControllerProps,
  useFormContext,
} from "react-hook-form";
import {
  IMAGE_MIME_TYPES,
  MIME_TYPE,
  MIME_TYPES_TO_EXTENSIONS,
} from "common/constants";
import { useSingleFileUpload } from "../../hooks/useSingleFileUpload";
import {
  getAllowedExtensions,
  mergeFileValidationRules,
  validFileExtension,
  validFileSize,
} from "../../utils/fileUploadValidation";
import Icon, { Icons } from "../Common/Icons";
import Illustration from "../Common/Illustration";
import {
  ImageDropzone,
  ImageDropzoneContent,
  ImageDropzoneHelperText,
  ImageDropzoneMainText,
  ImagePreview,
  ImagePreviewContainer,
  ImageRemoveButton,
} from "./__styles__/ImageUpload";
import Wrapper from "./Wrapper";

export type Props<
  TFieldValues extends FieldValues,
  Path extends FieldPath<TFieldValues>,
> = Omit<UseControllerProps<TFieldValues, Path>, "control"> & {
  control: UseControllerProps<TFieldValues, Path>["control"];
  allowedMimeTypes?: Array<MIME_TYPE>;
  fileHeader?: React.ReactNode;
  useDropzone?: (
    args: Pick<NonNullable<Parameters<typeof useReactDropzone>[0]>, "onDrop">,
  ) => ReturnType<typeof useReactDropzone>;
  label?: string;
  required?: boolean;
  description?: string;
  maxSizeMB?: number;
};

export const ReactHookFormImageUpload = <
  Form extends FieldValues,
  Path extends FieldPathByValue<Form, { blob: any }>,
>({
  useDropzone = undefined,
  allowedMimeTypes = [...IMAGE_MIME_TYPES] as Array<MIME_TYPE>,
  name: fieldName,
  rules,
  control,
  label,
  required,
  description,
  maxSizeMB = 30,
}: Props<Form, Path>) => {
  const formContext = useFormContext<Form>();
  const formMethods =
    formContext ?
      {
        setValue: formContext.setValue,
        watch: formContext.watch,
        resetField: formContext.resetField,
        control: formContext.control,
      }
    : undefined;

  const { currentValue, onDrop, resetField } = useSingleFileUpload<Form>(
    fieldName,
    formMethods,
  );

  const allowedExtensions = getAllowedExtensions(allowedMimeTypes);

  const DropzoneContent = () => (
    <ImageDropzoneContent>
      <Illustration name="add-file" style={{ width: "80px", height: "80px" }} />
      <div>
        <ImageDropzoneMainText>
          Drop your file here, or <span>click to browse</span>
        </ImageDropzoneMainText>
        <ImageDropzoneHelperText>
          JPG, JPEG or PNG, up to {maxSizeMB} MB max
        </ImageDropzoneHelperText>
      </div>
    </ImageDropzoneContent>
  );

  const validate = mergeFileValidationRules<Form[Path]>(rules, {
    "file-extension": (file: Form[Path]) =>
      validFileExtension(
        file as { originalFilename: string; blob: unknown },
        allowedExtensions,
      ),
    "file-size": (file: Form[Path]) =>
      validFileSize(file as { blob: unknown }, maxSizeMB),
  });

  const maxSizePerMimeType = allowedMimeTypes.reduce(
    (acc, mimeType) => {
      acc[mimeType] = maxSizeMB;
      return acc;
    },
    {} as Record<MIME_TYPE, number>,
  );

  const accept = {} as Record<string, string[]>;
  allowedMimeTypes.forEach(
    type => (accept[type] = MIME_TYPES_TO_EXTENSIONS[type as MIME_TYPE]),
  );

  const maxSize =
    maxSizePerMimeType ?
      Math.max(...Object.values<number>(maxSizePerMimeType)) * 1024 * 1024
    : undefined;

  const dropzoneHook = useDropzone || useReactDropzone;
  const { getRootProps, getInputProps } = dropzoneHook({
    onDrop,
    maxSize,
    accept,
    minSize: 1,
  });

  return (
    <Controller
      control={control}
      name={fieldName}
      rules={{ validate, ...rules }}
      render={({ fieldState }) => {
        return (
          <Wrapper
            name={fieldName}
            label={label}
            required={required}
            description={description}
            error={fieldState.error?.message}
          >
            {!currentValue?.blob ?
              <ImageDropzone
                {...getRootProps({
                  role: "button",
                  "aria-label": "Upload image",
                })}
              >
                <input {...getInputProps()} />
                <DropzoneContent />
              </ImageDropzone>
            : <ImagePreviewContainer>
                <ImagePreview
                  src={currentValue.blob.url}
                  alt={currentValue.originalFilename || "Uploaded image"}
                />
                <ImageRemoveButton
                  type="button"
                  onClick={resetField}
                  aria-label="Remove image"
                >
                  <Icon icon={Icons.CIRCLE_X} />
                </ImageRemoveButton>
              </ImagePreviewContainer>
            }
          </Wrapper>
        );
      }}
    />
  );
};
